import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { FormSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/FormSelectCombobox";
import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import type { IRelativeDateRange, RelativeRange, RelativeRangeType } from "app/pages/sales-intelligence/types/range";

import type { IFilterViewBaseModelProps } from "../../../types/filterInstance";
import type { DynamicRelativeDateRangeOptions } from "../../../types/filterOptions";
import advancedFilteringI18nKeys from "../../../utils/i18n";

import { translateOption } from "./utils";
import { StyledInnerWraper } from "./styles";

const RANGE_TYPE_FILTER_WIDTH = 117;
const RANGE_FILTER_WIDTH = 138;

const RelativeDateRangeFilter = ({
    filter: {
        options: { ranges, rangeTypes, label },
    },
    value,
    isTopLayer,
    onFilterUpdate,
}: IFilterViewBaseModelProps<IRelativeDateRange, DynamicRelativeDateRangeOptions>) => {
    const { translate } = useTranslate();

    const [rangeType, setRangeType] = useState<RelativeRangeType | undefined>(rangeTypes?.[0]);

    // Hydrate from the saved value first so a persisted filter shows its stored rangeType,
    // falling back to local state for a fresh selection before anything is saved.
    const selectedRangeType = value?.rangeType ?? rangeType;

    const handleRangeTypeChange = (newValue: RelativeRangeType): void => {
        setRangeType(newValue);

        if (!value?.range) {
            return;
        }

        onFilterUpdate({ ...value, rangeType: newValue });
    };

    const handleRangeChange = (newValue: RelativeRange) => {
        if (!selectedRangeType) {
            return;
        }

        onFilterUpdate({ range: newValue, rangeType: selectedRangeType });
    };

    return (
        <FormFieldLabel text={label ?? translate(advancedFilteringI18nKeys.relativeDateRange.defaultLabel)}>
            <StyledInnerWraper>
                <FormSelectCombobox<RelativeRangeType>
                    width={RANGE_TYPE_FILTER_WIDTH}
                    fieldName="relativeDateRangeType"
                    value={selectedRangeType}
                    options={rangeTypes}
                    formatValue={translateOption}
                    isTopLayer={isTopLayer}
                    onChange={handleRangeTypeChange}
                />

                <FormSelectCombobox<RelativeRange>
                    width={RANGE_FILTER_WIDTH}
                    fieldName="relativeDateRange"
                    value={value?.range}
                    options={ranges}
                    placeholder={translate(advancedFilteringI18nKeys.relativeDateRange.placeholder)}
                    formatValue={translateOption}
                    isTopLayer={isTopLayer}
                    onChange={handleRangeChange}
                />
            </StyledInnerWraper>
        </FormFieldLabel>
    );
};

export default RelativeDateRangeFilter;
